<template>
  <div class="initial-connection" data-testid="connection-content-container">
    <div class="label-and-field" data-testid="connection-container">
      <div>
        <label>Bag connects to</label>
        <help-tooltip v-if="initialIvSiteError" type="error" aria-label="IV Site Not Enabled Error" data-testid="iv-site-not-enabled-error">
          {{ initialIvSiteError.message }}
        </help-tooltip>
        <help-tooltip v-if="bagConnectedError" type="error" aria-label="Missing Bag Connection Error" data-testid="missing-bag-connection-error">
          {{ bagConnectedError.message }}
        </help-tooltip>
      </div>
      <md-field>
        <md-select
          class="connection-select"
          :value="connection ? connection.id : null"
          :disabled="readonly"
          placeholder="Select location/bag"
          aria-label="Connection"
          data-testid="connection-select"
          @input="updateConnection"
        >
          <md-option
            v-for="connectionSite in availableConnectionSites"
            :key="connectionSite.id"
            :value="connectionSite.id"
            :aria-label="connectionSite.label"
            data-testid="connection-option"
          >
            {{ connectionSite.label }}
          </md-option>
        </md-select>
        <help-tooltip v-if="connectionMedError" type="error" aria-label="Connection Medication Not Enabled Error" data-testid="connection-med-not-enabled-error">
          {{ connectionMedError.message }}
        </help-tooltip>
      </md-field>
    </div>
    <div
      v-if="isIvConnection"
      class="label-and-field"
      data-testid="port-container"
    >
      <label>Port</label>
      <md-field>
        <md-select
          class="port-select"
          :value="connection.port"
          :disabled="readonly"
          placeholder="Select a port"
          aria-label="Port"
          data-testid="port-select"
          @input="updatePort"
        >
          <md-option
            v-for="port in availablePorts"
            :key="port.key"
            :value="port.key"
            :aria-label="port.label.en"
            data-testid="port-option"
          >
            {{ port.label.en }}
          </md-option>
        </md-select>
        <help-tooltip v-if="initialIvPortError" type="error" aria-label="Port Not Enabled Error" data-testid="port-not-enabled-error">
          {{ initialIvPortError.message }}
        </help-tooltip>
      </md-field>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import { portAmounts, portNames } from '~lib/common/config/iv';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import { initialIvLabel } from '../../CaseIVs/IVOnAtStart/IVOnAtStartPanel.vue';
import { initialMedicationLabel } from './initialMedicationLabel';

export default {
  name: 'initial-connection',
  components: {
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    medications: {
      type: Array,
      required: true,
    },
    initialMedication: {
      type: Object,
      required: true
    },
    connection: {
      type: Object,
      required: true
    },
  },
  computed: {
    availableConnectionSites() {
      const ivConnectionSites = (this.caseConf.initialIvs || [])
        .filter(initialIv => this.isSiteAvailable(initialIv))
        .map(initialIv => {
          return {
            label: initialIvLabel(initialIv),
            id: initialIv.id,
          }
        });

      const medFluidConnectionSites = (this.caseConf.initialMedications || [])
        .filter((initialMed) => {
          const isAvail = this.isBagAvailable(initialMed);
          const notFirst = !this.isFirstBag;
          return isAvail && notFirst;
        })
        .map(initialMedication => ({
          label: initialMedicationLabel(
            initialMedication,
            this.allMedicationsById,
            this.caseConf,
          ),
          id: initialMedication.id,
        }));

      return ivConnectionSites.concat(medFluidConnectionSites);
    },
    availablePorts() {
      let ports = [];
      if (this.connection.type === 'IV_SITE') {
        const initIv = (this.caseConf.initialIvs || []).find(iv => iv.id === this.connection.id);
        if (initIv) {
          const portInfo = portAmounts.find(pa => pa.key === initIv.portAmount);
          if (portInfo) {
            let portKeys;
            if (portInfo.categoryPorts && portInfo.categoryPorts[initIv.categoryType]) {
              portKeys =  portInfo.categoryPorts[initIv.categoryType];
            } else {
              portKeys =  portInfo.defaultPorts;
            }
            ports = portNames.filter(
              portName => portKeys.includes(portName.key)
            );

            // Find any other initial meds that use the same IV connection
            const otherMeds = (this.caseConf.initialMedications || []).filter(otherMed => {
              if (otherMed.id !== this.initialMedication.id) {
                if (otherMed.connection && otherMed.connection.id === this.connection.id) {
                  return true;
                }
              }
            });
            // Filter out ports used by the other IV connections
            ports = ports.filter(port => {
              return !otherMeds.find(om => om.connection.port === port.key);
            });
          }
        }
      }
      // If current port not available, add it so that user can see their selection
      if (!ports.find(port => port.key === this.connection.port)) {
        const portName = portNames.find(pn => pn.key === this.connection.port);
        if (portName) {
          ports.push(portName)
        }
      }
      return ports;
    },
    allMedicationsById() {
      const allMeds = this.medications.concat(this.caseConf.customMedications || []);
      return keyBy(allMeds, 'id');
    },
    isFirstBag() {
      return (this.caseConf.initialMedications[0].id === this.initialMedication.id);
    },
    isIvConnection() {
      return this.connection.type === 'IV_SITE';
    },
    initialMedErrors() {
      return this.$store
        ? get(
          this.$store.getters['validation/errorsByFieldAndId'],
          `initialMedications.${this.initialMedication.id}`
        ) || [] : [];
    },
    initialIvSiteError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'initialIvSiteEnabled');
    },
    initialIvPortError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'initialIvPortEnabled');
    },
    connectionMedError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'connectionMed');
    },
    bagConnectedError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'bagConnected');
    },
  },
  methods: {
    isSiteAvailable(initialIv) {
      if (initialIv.id === this.connection.id) {
        return true;
      }
      const otherConnectionCount = (this.caseConf.initialMedications || []).reduce(
        (accum, otherInitMed) => {
          if (
            otherInitMed.id !== this.initialMedication.id &&
            otherInitMed.connection && otherInitMed.connection.id === initialIv.id
          ) {
            return accum + 1;
          }
          return accum;
        }, 0
      );
      const portAmount = portAmounts.find(pa => pa.key === initialIv.portAmount);
      const portCount = portAmount ? portAmount.portCount : 1;
      return otherConnectionCount < portCount;
    },
    isBagAvailable(initialMed) {
      const isDifferntBag = initialMed.id !== this.initialMedication.id;
      const isOnPatient = initialMed.connection && initialMed.connection.type === 'IV_SITE';
      // See if connected to another bag that isn't this bag
      const otherBagConnected = this.caseConf.initialMedications.find(
        otherBag => otherBag.id !== this.initialMedication.id &&
          otherBag.connection && otherBag.connection.id === initialMed.id
      );
      return isDifferntBag && isOnPatient && !otherBagConnected;
    },
    updateConnection(connectionId) {
      let type;

      // Look for id in the initial ivs
      const initIvs = (this.caseConf.initialIvs || []);
      const iv = initIvs.find(initIv => initIv.id === connectionId);
      if (iv) {
        type = 'IV_SITE';
      }

      // Look for id in the initial meds
      const initMeds = (this.caseConf.initialMedications || []);
      const initMed = initMeds.find(initMed => initMed.id === connectionId);
      if (initMed) {
        type = 'MEDICATION_OR_FLUID';
      }

      this.$emit('update', {
        ...this.connection,
        type,
        id: connectionId,
        port: null // clear when changing target
      });
    },
    updatePort(port) {
      const update = {
        ...this.connection,
        port
      };
      this.$emit('update', update);
    },
  }
}
</script>

<style scoped>
.initial-connection {
  display: flex;
  flex-direction: row;
}

.label-and-field {
  display: flex;
  flex-direction: column;
}

.label-and-field + .label-and-field {
  margin-left: 16px;
}

.connection-select {
  min-width: 400px;
}
</style>